import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { adminFetch } from './useAdminApi';

/**
 * Master activity feed (admin-activity-log) — orders, payments, staff
 * changes, and every outbound communication, merged chronologically.
 * Rows can be archived (soft-hidden, restorable from the Archived view) —
 * presentation state only, nothing in the source tables changes.
 */

export type ActivityCategory = 'order' | 'payment' | 'change' | 'comm';

export interface ActivityRow {
  /** Stable identity for archive/unarchive. */
  key: string;
  ts: string;
  category: ActivityCategory;
  action: string;
  actor: string;
  customer_id: string | null;
  customer_name: string | null;
  summary: string;
  status: string | null;
  ref: string | null;
}

interface ActivityPage {
  ok: true;
  rows: ActivityRow[];
  next_before: string | null;
}

export function useActivityLog(opts: {
  category?: ActivityCategory;
  customerId?: string;
  limit?: number;
  enabled?: boolean;
  /** true = show ONLY archived rows (the Archived view). */
  archived?: boolean;
}) {
  const { category, customerId, limit = 60, enabled = true, archived = false } = opts;
  return useInfiniteQuery({
    queryKey: [
      'admin',
      'activity-log',
      category ?? 'all',
      customerId ?? 'all',
      limit,
      archived ? 'archived' : 'live',
    ],
    enabled,
    staleTime: 30 * 1000,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      params.set('limit', String(limit));
      if (category) params.set('category', category);
      if (customerId) params.set('customer_id', customerId);
      if (archived) params.set('archived', '1');
      if (pageParam) params.set('before', pageParam);
      return adminFetch<ActivityPage>(`admin-activity-log?${params.toString()}`);
    },
    getNextPageParam: (last) => last.next_before,
  });
}

/** Archive or restore feed rows by key. Invalidate covers both the live and
 * archived views (and the per-customer comms panels — same query root). */
export function useArchiveActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { action: 'archive' | 'unarchive'; keys: string[] }) => {
      return adminFetch<{ ok: true; action: string; count: number }>('admin-activity-log', {
        method: 'POST',
        body: JSON.stringify(vars),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'activity-log'] });
    },
  });
}
